import React, { useState } from 'react';
import { Play, X, Youtube } from 'lucide-react';

// TABOR YouTube short form content - derived from Cinematic Trailer and Proof-of-concept
const taborVideos = [
  { id: 1, videoId: 'l5qDw6-hdh0' },
  { id: 2, videoId: 'tVwaMwJpB2M' },
  { id: 3, videoId: '1XX6v4hEH08' },
  { id: 4, videoId: 'cQh3M6LIDT8' },
  { id: 5, videoId: 'IEymLIG02UA' },
  { id: 6, videoId: '7cwTuEOD1hU' },
  { id: 7, videoId: '8ZEb15-Tt0s' },
  { id: 8, videoId: 'C764OAQW-3M' },
  { id: 9, videoId: 'psGZeZ17YeQ' },
  { id: 10, videoId: 'cv5-20Ub5mA' },
  { id: 11, videoId: '4mgMf3HVfR0' },
  { id: 12, videoId: '595mgSV0uvY', isShort: true }
];

const TaborSection = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
  };

  const closeModal = () => {
    setSelectedVideo(null);
  };

  const getYouTubeThumbnail = (videoId) => {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  const getYouTubeEmbedUrl = (videoId, isShort = false) => {
    // YouTube Shorts can be embedded the same way as regular videos
    return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
  };

  return (
    <section className="py-20 px-4 bg-[#061018] relative overflow-hidden">
      {/* Abstract background elements */}
      <div className="absolute inset-0 opacity-15 pointer-events-none">
        <div className="abstract-blob animate-float" style={{ 
          top: '10%', 
          right: '10%', 
          width: '400px', 
          height: '400px', 
          background: 'radial-gradient(circle, rgba(255, 101, 0, 0.25), transparent 70%)',
          filter: 'blur(80px)'
        }}></div>
        <div className="abstract-blob animate-float-delayed" style={{ 
          bottom: '15%', 
          left: '5%', 
          width: '350px', 
          height: '350px', 
          background: 'radial-gradient(circle, rgba(255, 140, 50, 0.2), transparent 70%)',
          filter: 'blur(75px)'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-sm text-[#FF6500] uppercase tracking-widest mb-2">
            Highlighted Digital Content Campaign with Augmented Cinematics
          </p>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-4">
            TABOR: <span className="text-[#FF6500]">A Christmas Tale</span>
          </h2>
          <p className="text-xl text-[#aaa] max-w-3xl mx-auto mb-8">
            These short form contents were derived from the visuals created from the Cinematic Trailer and Proof-of-concept.
          </p>
          
          {/* Subscribe Button */}
          <div className="flex flex-col items-center gap-4">
            <p className="text-[#aaa] text-sm">
              Want consistent updates on new content for this project?
            </p>
            <a 
              href="https://www.youtube.com/@TABORAChristmasTale?sub_confirmation=1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#FF0000] hover:bg-[#CC0000] text-white font-medium rounded-full transition-all duration-300 text-base uppercase tracking-wider hover:scale-105 shadow-lg shadow-red-500/20"
            >
              <Youtube className="w-6 h-6" />
              Subscribe to Channel
            </a>
          </div>
        </div>

        {/* Video Gallery - Plain grid without titles */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {taborVideos.map((video) => (
            <div 
              key={video.id}
              onClick={() => handleVideoClick(video)}
              className="group cursor-pointer rounded-lg overflow-hidden hover:transform hover:-translate-y-1 transition-all duration-300 border border-[#152840] hover:border-[#FF6500]/50 hover:shadow-lg hover:shadow-[#FF6500]/10"
            >
              <div className="relative aspect-video overflow-hidden bg-black">
                <img 
                  src={getYouTubeThumbnail(video.videoId)} 
                  alt="Video thumbnail"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    e.target.src = `https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`;
                  }}
                />
                {/* Play button overlay */}
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                  <div className="w-12 h-12 bg-[#FF6500] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-[#FF6500]/30">
                    <Play className="w-6 h-6 text-black ml-1" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 animate-fadeIn"
          onClick={closeModal}
        >
          <div 
            className="relative w-full max-w-5xl animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute -top-12 right-0 p-2 text-white hover:text-[#FF6500] transition-colors duration-300"
              aria-label="Close video"
            >
              <X className="w-8 h-8" />
            </button>
            
            <div className="aspect-video w-full bg-black rounded-lg overflow-hidden shadow-2xl shadow-[#FF6500]/20">
              <iframe
                src={getYouTubeEmbedUrl(selectedVideo.videoId, selectedVideo.isShort)}
                title="TABOR Video"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default TaborSection;
